import React, {Component} from "react";
import {Dialog} from "primereact/dialog";
import {Checkbox} from "primereact/checkbox";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import moment from "moment";
import RemotingService from "../../../../../services/remoting-service/RemotingService";

const reportStyle = {
    backgroundColor: '#f5f5f5',
    padding: '10px',
    maxHeight: '300px',
    overflow: 'auto',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
};

// claimId -> checklist number -> persisted state ({number, checked, staffName, updateDate})
const manualStatesOf = (result) => {
    const byClaim = {};
    ((result && result.audits) || []).forEach(audit => {
        byClaim[audit.id] = {};
        (audit.manualAudit || []).forEach(state => {
            byClaim[audit.id][state.number] = state;
        });
    });
    return byClaim;
};

const mergeStates = (byClaim, states) => {
    const merged = {...byClaim};
    (states || []).forEach(state => {
        merged[state.claimId] = {...(merged[state.claimId] || {}), [state.number]: state};
    });
    return merged;
};

/**
 * Result of a Shafafiya claim check or a blocked submission: the internal audit (automatic checklist
 * rows per claim), the Shafafiya validate-only verdict, and the manual checklist rows whose ticks are
 * persisted per claim (non-blocking reminders).
 */
export default class ClaimCheckerResultDialog extends Component {

    constructor(props) {
        super(props);
        this.state = {manualByClaim: manualStatesOf(props.result)};
    }

    componentDidUpdate(prevProps) {
        // Every check returns a fresh result object carrying the persisted ticks
        if (prevProps.result !== this.props.result) {
            this.setState({manualByClaim: manualStatesOf(this.props.result)});
        }
    }

    claimIds() {
        return ((this.props.result && this.props.result.audits) || []).map(audit => audit.id);
    }

    // Row is ticked only when every claim in the dialog has it ticked
    manualRowState(number) {
        const claimIds = this.claimIds();
        const states = claimIds
            .map(claimId => (this.state.manualByClaim[claimId] || {})[number])
            .filter(state => state && state.checked);
        return {
            total: claimIds.length,
            checkedCount: states.length,
            allChecked: claimIds.length > 0 && states.length === claimIds.length,
            info: states.length === 1 ? states[0] : null
        };
    }

    toggleManualCheck = (number, checked) => {
        const claimIds = this.claimIds();
        if (claimIds.length === 0) {
            return;
        }
        const previous = this.state.manualByClaim;
        // Optimistic: reflect the tick at once, roll back if the save fails
        this.setState(prevState => ({
            manualByClaim: mergeStates(prevState.manualByClaim, claimIds.map(claimId => ({claimId, number, checked})))
        }));
        RemotingService.putRemoteCall('api/insurance/coordinator/v2/claims/manual-audit',
            {claimIds, number, checked},
            (states) => this.setState(prevState => ({manualByClaim: mergeStates(prevState.manualByClaim, states)})),
            () => this.setState({manualByClaim: previous}));
    }

    renderAudit(audit) {
        const checks = audit.checks || [];
        const failedCount = audit.failedCount || 0;
        return (
            <div key={audit.id || audit.claimId} className="mb-3">
                <div>
                    <strong>{audit.claimId}</strong>
                    {audit.patientName && <span className="ml-2">{audit.patientName}</span>}
                    {audit.mrn && <span className="text-muted ml-2">(MRN {audit.mrn})</span>}
                    {checks.length > 0 && (
                        <span className={`ml-2 small ${failedCount === 0 ? 'text-success' : 'text-danger'}`}>
                            {failedCount === 0
                                ? `All ${checks.length} checks passed`
                                : `${failedCount} of ${checks.length} checks failed`}
                        </span>
                    )}
                </div>
                <ul className="mt-1 mb-0" style={{listStyle: 'none', paddingLeft: 0}}>
                    {checks.map(check => (
                        <li key={check.field} className={check.valid ? 'text-success' : 'text-danger'} style={{padding: '2px 0'}}>
                            <FontAwesomeIcon icon={check.valid ? 'check' : 'times'}/>
                            <strong className="ml-2">{check.label}:</strong>
                            <span className="ml-1">
                                {check.value ? check.value : <em className="text-muted">(empty)</em>}
                            </span>
                            {!check.valid && (
                                <div className="text-muted small ml-4" style={{fontStyle: 'italic'}}>
                                    {check.message}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    renderManualChecks(manualChecks) {
        return (
            <div>
                <p className="text-muted small mb-2">
                    Tick what you have verified — saved per claim as a record, but never blocks the check or the submission.
                </p>
                {manualChecks.map(check => {
                    const inputId = `claim-manual-check-${check.number}`;
                    const row = this.manualRowState(check.number);
                    const partial = row.checkedCount > 0 && !row.allChecked;
                    return (
                        <div key={check.number} className="d-flex align-items-start mb-1">
                            <Checkbox inputId={inputId}
                                      checked={row.allChecked}
                                      onChange={event => this.toggleManualCheck(check.number, event.checked)}/>
                            <label htmlFor={inputId} className="ml-2 mb-0" style={{cursor: 'pointer'}}>
                                <strong>#{check.number}</strong> {check.description}
                                {partial && (
                                    <span className="text-muted small ml-1">({row.checkedCount} of {row.total} claims)</span>
                                )}
                                {row.allChecked && row.info && (
                                    <span className="text-muted small ml-1">
                                        — {row.info.staffName || 'unknown'}{row.info.updateDate ? `, ${moment(row.info.updateDate).format('DD/MM/YYYY HH:mm')}` : ''}
                                    </span>
                                )}
                            </label>
                        </div>
                    );
                })}
            </div>
        );
    }

    render() {
        const {visible, result, onClose, header} = this.props;
        if (!result) {
            return null;
        }

        const audits = result.audits || [];
        const manualChecks = result.manualChecks || [];
        const failedAuditChecks = audits.reduce((sum, audit) => sum + (audit.failedCount || 0), 0);
        const auditPassed = failedAuditChecks === 0;
        const hasWarnings = result.success && result.errorReport;

        // A failed internal audit stops the check before anything is sent to Shafafiya
        const headlineClass = !result.success ? 'text-danger' : hasWarnings ? 'text-warning' : 'text-success';

        return (
            <Dialog header={header || "Shafafiya Claim Check"} visible={visible} style={{width: '60vw'}}
                    contentStyle={{maxHeight: '75vh', overflow: 'auto'}}
                    onHide={onClose} dismissableMask={true}>
                <div className="p-3">
                    <p className={`font-weight-bold ${headlineClass}`}>{result.message}</p>

                    <h6 className="mt-3">Internal audit</h6>
                    {audits.length === 0 && <p className="text-muted small">No internal audit results.</p>}
                    {audits.map(audit => this.renderAudit(audit))}

                    <h6 className="mt-3">Shafafiya validation</h6>
                    {!auditPassed
                        ? <p className="text-muted">Skipped — not sent to Shafafiya until the internal audit passes.</p>
                        : <>
                            <p className={result.success ? 'text-success' : 'text-danger'}>{result.message}</p>
                            {result.transactionId && <p><strong>Transaction ID:</strong> {result.transactionId}</p>}
                            {result.errorReport &&
                            <div className="mt-2">
                                <strong>Report:</strong>
                                <pre style={reportStyle}>{result.errorReport}</pre>
                            </div>}
                        </>}

                    {manualChecks.length > 0 && audits.length > 0 && (
                        <>
                            <h6 className="mt-3">Manual checks</h6>
                            {this.renderManualChecks(manualChecks)}
                        </>
                    )}
                </div>
            </Dialog>
        );
    }
}
